import { useEffect, useRef } from 'react';
import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { isPrivateModeEnabled } from './privateMode';
import {
  SCRATCHPAD_MAX,
  ScratchItem,
  ScratchKind,
  addScratchItem,
  makeScratchId,
  removeScratchItem,
  setScratchThumbnail,
} from './scratchpadItems';

/**
 * The private-mode scratchpad: the last {@link SCRATCHPAD_MAX} things copied — "Use
 * Settings", prompt copies, plain text — so a prompt three copies ago is still
 * reachable instead of being lost to the single-slot system clipboard.
 *
 * It only ever holds private-session content, and does so in three ways:
 *  - {@link pushScratch} is a no-op while private mode is off, so nothing accumulates;
 *  - storage is sessionStorage, which dies with the tab rather than persisting to disk;
 *  - leaving private mode clears it (see PrivateModeToggle), and a rehydrate into a
 *    non-private session drops whatever was there.
 */

export interface ScratchEntry {
  kind: ScratchKind;
  text: string;
  thumbnail?: string;
  source?: string;
}

interface ScratchpadState {
  items: ScratchItem[];
  open: boolean;
  /** Whether the current page has a prompt field registered, so Apply has a destination. */
  canApply: boolean;
  setOpen: (open: boolean) => void;
  push: (entry: ScratchEntry) => void;
  remove: (id: string) => void;
  clear: () => void;
  setThumbnail: (id: string, thumbnail: string) => void;
}

export const useScratchpad = create<ScratchpadState>()(
  persist(
    (set) => ({
      items: [],
      open: false,
      canApply: false,
      setOpen: (open) => set({ open }),
      push: (entry) =>
        set((state) => {
          const createdAt = Date.now();
          const item: ScratchItem = {
            id: makeScratchId(entry.text.trim(), createdAt),
            kind: entry.kind,
            text: entry.text,
            thumbnail: entry.thumbnail,
            source: entry.source,
            createdAt,
          };
          return { items: addScratchItem(state.items, item) };
        }),
      remove: (id) => set((state) => ({ items: removeScratchItem(state.items, id) })),
      clear: () => set({ items: [] }),
      setThumbnail: (id, thumbnail) =>
        set((state) => ({ items: setScratchThumbnail(state.items, id, thumbnail) })),
    }),
    {
      name: 'comfy-scratchpad',
      storage: createJSONStorage(() =>
        typeof window !== 'undefined' ? window.sessionStorage : (undefined as unknown as Storage)
      ),
      // `open` is per-view chrome, not content worth restoring.
      partialize: (state) => ({ items: state.items }) as unknown as ScratchpadState,
      onRehydrateStorage: () => (state) => {
        // A tab that comes back without private mode must not surface private copies.
        if (state && !isPrivateModeEnabled()) state.items = [];
      },
    }
  )
);

/**
 * Where "Apply" sends text. The page that owns a prompt field registers itself, so the
 * header panel can drop an entry straight into it without knowing which page is open.
 * Only one page is mounted at a time, so a single slot is enough.
 */
type ApplyTarget = (text: string) => void;
let applyTarget: ApplyTarget | null = null;

/** Returns a disposer; it only clears the slot if this target is still the current one. */
function registerScratchpadApply(fn: ApplyTarget): () => void {
  applyTarget = fn;
  useScratchpad.setState({ canApply: true });
  return () => {
    if (applyTarget === fn) {
      applyTarget = null;
      useScratchpad.setState({ canApply: false });
    }
  };
}

/**
 * Offer this page's prompt field as the Apply destination. Pass null on pages that
 * have none — the panel hides the button rather than offering a dead action.
 */
export function useScratchpadApplyTarget(apply: ApplyTarget | null): void {
  const latest = useRef<ApplyTarget | null>(apply);
  latest.current = apply;
  const active = apply !== null;

  useEffect(() => {
    if (!active) return;
    return registerScratchpadApply((text) => latest.current?.(text));
  }, [active]);
}

/** Send text to the registered prompt field. False when the page has none. */
export function applyScratch(text: string): boolean {
  if (!applyTarget) return false;
  applyTarget(text);
  return true;
}

/**
 * Record something the user copied. Silently does nothing outside private mode —
 * every call site can stay unconditional.
 */
export function pushScratch(entry: ScratchEntry): void {
  if (!isPrivateModeEnabled()) return;
  useScratchpad.getState().push(entry);
}

/** Wipe the scratchpad. Called when private mode is switched off. */
export function clearScratchpad(): void {
  useScratchpad.getState().clear();
  useScratchpad.getState().setOpen(false);
}

/**
 * Put `text` on the system clipboard and record it. Returns whether the clipboard
 * write succeeded — it can fail on an insecure origin or without focus, and the
 * scratchpad entry is still worth keeping when it does.
 */
export async function copyWithScratch(text: string, entry?: Partial<ScratchEntry>): Promise<boolean> {
  let copied = false;
  try {
    await navigator.clipboard.writeText(text);
    copied = true;
  } catch {
    copied = false;
  }
  pushScratch({ kind: entry?.kind ?? 'text', text, thumbnail: entry?.thumbnail, source: entry?.source });
  return copied;
}

export { SCRATCHPAD_MAX };
export type { ScratchItem, ScratchKind };
